import React, { useState } from 'react';
import { Clock, X, AlertCircle } from 'lucide-react';

/**
 * CheckinReasonModal — pops on the staff app when the user attempts a
 * **late check-in** or **early check-out**. Forces them to pick a reason
 * (or supply a ≤25-char custom note) before the action can complete.
 *
 * Backend codes are the source of truth (see `models/__init__.py
 * ::ATTENDANCE_REASON_CODES`). Labels here mirror those in
 * `routes/reports.py::REASON_LABELS` so admin reports show the same wording.
 *
 * Props:
 *   mode        - 'checkin' | 'checkout'
 *   minutesOff  - integer; how many minutes late (checkin) / early (checkout)
 *   scheduled   - 'HH:MM' string from the user's day_checkin_time / day_checkout_time
 *   onCancel()  - called when the staff dismisses without picking
 *   onConfirm({reason_code, reason_other_text})  - called with the chosen payload
 *   submitting  - disable buttons while parent is calling the API
 */

// SAME 8 reasons for both LATE CHECK-IN and EARLY CHECK-OUT (per user
// requirement Feb 22, 2026 — option 1.c). Codes kept in sync with backend
// `models/__init__.py::ATTENDANCE_REASON_CODES`.
const REASON_OPTIONS = [
  { code: 'late_checkout_after_10pm',   label: 'Yesterday late check-out after 10 PM' },
  { code: 'late_checkout_after_12am',   label: 'Yesterday late check-out after 12 AM' },
  { code: 'late_checkout_after_1am',    label: 'Yesterday late check-out after 1 AM' },
  { code: 'late_checkout_after_2am',    label: 'Yesterday late check-out after 2 AM' },
  { code: 'late_checkout_after_3to6am', label: 'Yesterday late check-out 3 AM to 6 AM' },
  { code: 'morning_permission',         label: 'Morning permission' },
  { code: 'early_leaving_permission',   label: 'Early leaving permission' },
  { code: 'other',                      label: 'OTHER (type below, ≤25 chars)' },
];

export default function CheckinReasonModal({
  mode,
  minutesOff,
  scheduled,
  onCancel,
  onConfirm,
  submitting = false,
}) {
  const [code, setCode] = useState('');
  const [other, setOther] = useState('');
  const [touched, setTouched] = useState(false);

  const options = REASON_OPTIONS;
  const title = mode === 'checkout' ? 'Early Check-out' : 'Late Check-in';
  const action = mode === 'checkout' ? 'Check-out' : 'Check-in';
  const isOther = code === 'other';
  const otherTrimmed = (other || '').trim();
  const canConfirm =
    !!code &&
    (!isOther || (otherTrimmed.length > 0 && otherTrimmed.length <= 25));

  const submit = () => {
    setTouched(true);
    if (!canConfirm) return;
    onConfirm({
      reason_code: code,
      reason_other_text: isOther ? otherTrimmed : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-2"
      data-testid="reason-modal"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto overscroll-contain">
        <div className="px-5 py-4 border-b flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Clock size={18} className="text-amber-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight">{title}</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Scheduled: <b>{scheduled}</b>
                {' · '}
                {mode === 'checkout' ? 'Leaving early by' : 'Late by'}{' '}
                <b data-testid="reason-modal-mins">{minutesOff} min</b>
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-gray-100 rounded-lg shrink-0"
            data-testid="reason-modal-close-btn"
            disabled={submitting}
            aria-label="Cancel"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-900">
            Pick the reason that best matches your situation. This is recorded
            on your attendance row and appears in the Late Comers / Early
            Leavers reports.
          </div>

          <fieldset className="space-y-1.5">
            <legend className="sr-only">Choose a reason</legend>
            {options.map((o) => (
              <label
                key={o.code}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm select-none ${
                  code === o.code
                    ? 'bg-blue-50 border-blue-400 text-blue-900'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
                data-testid={`reason-option-${o.code}`}
              >
                <input
                  type="radio"
                  name="reason"
                  value={o.code}
                  checked={code === o.code}
                  onChange={() => setCode(o.code)}
                  className="mt-0.5 accent-blue-600"
                />
                <span className="flex-1 leading-snug">{o.label}</span>
              </label>
            ))}
          </fieldset>

          {isOther && (
            <div className="space-y-1">
              <input
                type="text"
                value={other}
                onChange={(e) => setOther(e.target.value.slice(0, 25))}
                placeholder="Short reason (max 25 characters)"
                maxLength={25}
                autoFocus
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm"
                data-testid="reason-other-input"
              />
              <p className="text-[11px] text-gray-400 text-right">
                {otherTrimmed.length} / 25
              </p>
            </div>
          )}

          {touched && !canConfirm && (
            <div
              className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2.5 text-[12px] text-red-700"
              data-testid="reason-modal-error"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                {isOther
                  ? 'Please type a short reason (1-25 characters).'
                  : 'Please choose a reason to continue.'}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium disabled:opacity-50"
              data-testid="reason-modal-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !canConfirm}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50"
              data-testid="reason-modal-confirm-btn"
            >
              {submitting ? 'Submitting…' : `Confirm ${action}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
